import type React from 'react';

/**
 * Helpers for keeping money / quantity inputs numeric.
 *
 * Two layers, used together on controlled inputs:
 *  - `blockNonNumericInput` rejects disallowed characters at the source via the
 *    native `beforeinput` event (covers typing, paste, and IME commits) so they
 *    never reach the value. Deletions and caret navigation carry null data and
 *    pass through untouched, and arrow-key stepping never fires `beforeinput`,
 *    so it composes safely with Fluent's SpinButton.
 *  - `sanitizeDecimalString` is the controlled-value backstop: it strips any
 *    non-numeric characters and collapses to a single decimal point, so even a
 *    value that slips past the guard can't persist in the field.
 */

/** Strip everything but digits, keeping at most one decimal point. */
export const sanitizeDecimalString = (value: string): string => {
  let v = value.replace(/[^0-9.]/g, '');
  const dot = v.indexOf('.');
  if (dot !== -1) {
    v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
  }
  return v;
};

/**
 * `beforeinput` guard: prevent any inserted text that isn't a digit (and, when
 * `allowDecimal`, a decimal point). Wire to an input's `onBeforeInput`.
 */
export const blockNonNumericInput = (
  e: React.FormEvent<HTMLInputElement>,
  allowDecimal: boolean,
): void => {
  const data = (e.nativeEvent as InputEvent).data;
  if (data == null) return;
  const allowed = allowDecimal ? /^[0-9.]+$/ : /^[0-9]+$/;
  if (!allowed.test(data)) e.preventDefault();
};
